import { useRef, useCallback, useEffect, useState } from 'react';
import Tooltip from './ui/Tooltip';

/**
 * RichTextArea — contentEditable div with bold and bullet mode.
 * Stores HTML (supports <b>, <br>, newlines).
 * Bullet mode inserts "• " at the start of each new line.
 * Works with Google Calendar descriptions which accept HTML.
 */
const ALLOWED_TAGS = ['b', 'strong', 'br', 'div'];
function sanitizeHTML(html) {
  const tagPattern = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/gi;
  return html.replace(tagPattern, (match, tag) =>
    ALLOWED_TAGS.includes(tag.toLowerCase()) ? match : ''
  );
}

const RichTextArea = ({ value, onChange, rows = 4, placeholder = '' }) => {
  const editorRef = useRef(null);
  const isInternalUpdate = useRef(false);
  const [listMode, setListMode] = useState(null); // null | 'bullet' | 'numbered'

  // Sync external value changes (e.g. initial load) into the editor
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    if (editorRef.current && editorRef.current.innerHTML !== (value || '')) {
      editorRef.current.innerHTML = sanitizeHTML(value || '');
    }
  }, [value]);

  const handleInput = useCallback(() => {
    if (!editorRef.current) return;
    isInternalUpdate.current = true;
    const html = editorRef.current.innerHTML;
    const isEmpty = html.replace(/<br\s*\/?>/gi, '').replace(/<[^>]*>/g, '').trim() === '';
    onChange(isEmpty ? '' : html);
  }, [onChange]);

  const savedSelection = useRef(null);

  const saveSelection = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
      savedSelection.current = sel.getRangeAt(0).cloneRange();
    }
  }, []);

  const restoreSelection = useCallback(() => {
    if (savedSelection.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedSelection.current);
    }
  }, []);

  const handleBold = useCallback((e) => {
    e.preventDefault();
    editorRef.current?.focus();
    restoreSelection();
    document.execCommand('bold', false, null);
    handleInput();
  }, [handleInput, restoreSelection]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    handleInput();
  }, [handleInput]);

  const handleKeyDown = useCallback((e) => {
    // Ctrl/Cmd+B for bold
    if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
      e.preventDefault();
      document.execCommand('bold', false, null);
      handleInput();
      return;
    }
    // In list mode, Enter inserts prefix on the new line
    if (listMode && e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (listMode === 'bullet') {
        document.execCommand('insertText', false, '\n• ');
      } else {
        // Use full editor textContent to find the current line (handles bold/rich text nodes)
        const fullText = editorRef.current?.textContent || '';
        const sel = window.getSelection();
        let prevNum = 0;
        if (sel && sel.anchorNode) {
          // Get cursor offset within the full text
          const range = document.createRange();
          range.selectNodeContents(editorRef.current);
          range.setEnd(sel.anchorNode, sel.anchorOffset);
          const textBeforeCursor = range.toString();
          const lastNewline = textBeforeCursor.lastIndexOf('\n');
          const currentLine = lastNewline >= 0 ? textBeforeCursor.substring(lastNewline + 1) : textBeforeCursor;
          const match = currentLine.match(/^(\d+)\.\s/);
          if (match) prevNum = parseInt(match[1]);
        }
        document.execCommand('insertText', false, `\n${prevNum + 1}. `);
      }
      handleInput();
    }
  }, [handleInput, listMode]);

  const toggleListMode = useCallback((mode) => (e) => {
    e.preventDefault();
    const next = listMode === mode ? null : mode;
    setListMode(next);
    editorRef.current?.focus();
    restoreSelection();
    if (next) {
      const text = editorRef.current?.textContent || '';
      if (!text.trim()) {
        document.execCommand('insertText', false, next === 'bullet' ? '• ' : '1. ');
        handleInput();
      }
    }
  }, [listMode, restoreSelection, handleInput]);

  const minHeight = Math.max(rows * 24, 72);

  return (
    <div className="relative group/rta">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 mb-1.5 bg-gray-900/60 border border-gray-700/50 rounded-md p-0.5 w-fit">
        <Tooltip label="Bold (Ctrl+B)">
          <button
            type="button"
            onMouseDown={handleBold}
            className="w-7 h-7 flex items-center justify-center text-xs font-black text-on-surface-variant hover:text-white hover:bg-gray-700 rounded transition-colors duration-100"
          >
            B
          </button>
        </Tooltip>
        <div className="w-px h-4 bg-gray-700/60" />
        <Tooltip label="Bullet list">
          <button
            type="button"
            onMouseDown={toggleListMode('bullet')}
            aria-label="Bullet list"
            className={`w-7 h-7 flex items-center justify-center rounded transition-colors duration-100 ${
              listMode === 'bullet' ? 'text-primary-ink bg-primary/15' : 'text-on-surface-variant hover:text-white hover:bg-gray-700'
            }`}
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12M8.25 17.25h12M3.75 6.75h.007v.008H3.75V6.75zm0 5.25h.007v.008H3.75V12zm0 5.25h.007v.008H3.75v-.008z" />
            </svg>
          </button>
        </Tooltip>
        <Tooltip label="Numbered list">
          <button
            type="button"
            onMouseDown={toggleListMode('numbered')}
            className={`w-7 h-7 flex items-center justify-center rounded transition-colors duration-100 text-[10px] font-bold ${
              listMode === 'numbered' ? 'text-primary-ink bg-primary/15' : 'text-on-surface-variant hover:text-white hover:bg-gray-700'
            }`}
          >
            1.
          </button>
        </Tooltip>
      </div>

      {/* Editor */}
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onKeyUp={saveSelection}
          onMouseUp={saveSelection}
          onSelect={saveSelection}
          className="w-full px-3 py-2.5 bg-gray-900/80 border border-gray-700/60 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40 transition-all duration-150 overflow-y-auto"
          style={{ minHeight, whiteSpace: 'pre-wrap', wordBreak: 'break-word', resize: 'vertical' }}
          suppressContentEditableWarning
        />
        {/* Placeholder */}
        {!value && (
          <div className="absolute top-2.5 left-3 text-on-surface-disabled text-sm pointer-events-none select-none">
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
};

export default RichTextArea;
